import { useState } from "react";
import { Columns3, Rows3, Save, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { STAGES } from "@/lib/charts";

export const ALL_COLS = [
  ["rank", "Rank"], ["name", "Name"], ["resume_match", "Resume Match"], ["coverage", "Must-haves"], ["category", "Category"],
  ["stage", "Stage"], ["fit_score", "Fit Score"], ["tags", "Tags"], ["last_activity", "Last activity"],
];
export const DEFAULT_COLS = ["rank", "name", "fit_score", "resume_match", "coverage", "category", "stage"];
const CATS = ["Strong Fit", "Potential Fit", "Needs Review", "Not a Fit", "Not a Fit – Knockout"];

const Select = ({ value, onChange, options, label, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testId}
    className="h-9 rounded-lg border border-line bg-white px-2 text-sm text-ink-secondary">
    <option value="">{label}</option>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export function RankedToolbar({ jobId, view, setView, tags, compact, setCompact }) {
  const key = `tl_views_${jobId}`;
  const [views, setViews] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  const set = (k) => (v) => setView({ ...view, [k]: v });
  const saveView = () => {
    const name = window.prompt("Name this view");
    if (!name) return;
    const next = [...views.filter((v) => v.name !== name), { name, ...view }];
    localStorage.setItem(key, JSON.stringify(next));
    setViews(next);
    toast.success(`View "${name}" saved`);
  };
  const toggleCol = (c) => setView({ ...view, cols: view.cols.includes(c) ? view.cols.filter((x) => x !== c) : [...view.cols, c] });

  return (
    <div className="flex flex-wrap items-center gap-2" data-tour="pt-ranked-toolbar" data-testid="ranked-toolbar">
      <Select value={view.category} onChange={set("category")} label="All categories" options={CATS.map((c) => [c, c])} testId="filter-category" />
      <Select value={view.stage} onChange={set("stage")} label="All stages" options={STAGES} testId="filter-stage" />
      <Select value={view.tag} onChange={set("tag")} label="All tags" options={tags.map((t) => [t, t])} testId="filter-tag" />
      {views.length > 0 && (
        <Select value="" onChange={(n) => { const v = views.find((x) => x.name === n); if (v) { const { name, ...rest } = v; setView(rest); toast(`View "${name}" applied`); } }}
          label="Saved views" options={views.map((v) => [v.name, v.name])} testId="saved-views-select" />
      )}
      <Button variant="ghost" size="sm" onClick={saveView} data-testid="save-view-btn"><Save size={14} /> Save view</Button>
      <Popover>
        <PopoverTrigger asChild><Button variant="ghost" size="sm" data-tour="pt-column-picker-btn" data-testid="column-picker-btn"><Columns3 size={14} /> Columns</Button></PopoverTrigger>
        <PopoverContent className="w-52 space-y-2 rounded-[6px]" data-testid="column-picker">
          {ALL_COLS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={view.cols.includes(k)} onCheckedChange={() => toggleCol(k)} data-testid={`col-toggle-${k}`} /> {l}</label>
          ))}
        </PopoverContent>
      </Popover>
      <Button variant="ghost" size="sm" onClick={() => setCompact(!compact)} data-testid="compact-toggle"><Rows3 size={14} /> {compact ? "Comfortable" : "Compact"}</Button>
      <span className="ml-auto inline-flex items-center gap-1 text-xs text-ink-muted"><Star size={12} /> Fit Score (0–10) combines every completed stage</span>
    </div>
  );
}
